import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { Question, Result, Answer } from "../../shared/api";
import "./challenge.css";
import { prepareQuestion } from "./questionState";

export function optionFeedback(
  id: string,
  selected: string[],
  expected: string[],
) {
  return expected.includes(id)
    ? selected.includes(id)
      ? "correct"
      : "missed"
    : selected.includes(id)
      ? "wrong"
      : "";
}
const labels = {
  "select-many": "多选题",
  "match-pairs": "做配对",
  "order-tap": "排顺序",
  "grade-map": "分等级",
  "matrix-select": "矩阵多选",
};
function ImageAnalysis({ url, caption }: { url: string; caption?: string }) {
  const [failed, setFailed] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);
  return (
    <figure>
      {failed ? (
        <p>解析图片加载失败</p>
      ) : (
        <button
          className="image-open"
          aria-label="查看解析大图"
          onClick={() => dialog.current?.showModal()}
        >
          <img
            src={url}
            alt={caption || "题目解析"}
            onError={() => setFailed(true)}
          />
        </button>
      )}
      {caption && <figcaption>{caption}</figcaption>}
      <dialog
        ref={dialog}
        className="image-dialog"
        onClick={(e) => {
          if (e.target === e.currentTarget) dialog.current?.close();
        }}
      >
        <button
          autoFocus
          aria-label="关闭解析大图"
          onClick={() => dialog.current?.close()}
        >
          关闭
        </button>
        <img src={url} alt={caption || "题目解析大图"} />
      </dialog>
    </figure>
  );
}
export function Challenge({
  question,
  result,
  busy,
  onSubmit,
  onRedo,
  initialDraft = {},
  onDraftChange,
}: {
  question: Question;
  result?: Result;
  busy: boolean;
  onSubmit: (answer: Answer) => void;
  onRedo: () => void;
  initialDraft?: Answer;
  onDraftChange?: (answer: Answer) => void;
}) {
  const [view, setView] = useState(() => prepareQuestion(question));
  const c = view.content;
  const [draft, updateDraft] = useState<Answer>(initialDraft);
  const setDraft = (value: Answer) => {
    updateDraft(value);
    onDraftChange?.(value);
  };
  const sheet = useRef<HTMLDialogElement>(null);
  const [activeLeft, setActiveLeft] = useState<string>();
  const [activeRight, setActiveRight] = useState<string>();
  const [activeMatrix, setActiveMatrix] = useState(c.questions?.[0]?.id);
  const board = useRef<HTMLDivElement>(null);
  const [lines, setLines] = useState<
    {
      x1: number;
      y1: number;
      x2: number;
      y2: number;
      kind: string;
      key: string;
    }[]
  >([]);
  const answer = result?.response ?? draft;
  const expected = result?.correctResponse ?? {};
  const selected = answer.selectedItemIds ?? [];
  const pairs = answer.pairs ?? [];
  const order = answer.orderedItemIds ?? [];
  const grades = answer.grades ?? {};
  const matrix = answer.answers ?? [];
  const locked = busy || !!result;
  const feedback = (
    id: string,
    actual = selected,
    correct = expected.selectedItemIds ?? [],
  ) => (result ? optionFeedback(id, actual, correct) : "");
  const choosePair = (leftId: string, rightId: string) => {
    setDraft({
      ...draft,
      pairs: [
        ...(draft.pairs ?? []).filter((p) => p.leftId !== leftId),
        { leftId, rightId },
      ],
    });
    setActiveLeft(undefined);
    setActiveRight(undefined);
  };
  useLayoutEffect(() => {
    if (!board.current) return;
    const draw = () => {
      const root = board.current!;
      const bounds = root.getBoundingClientRect();
      const sets = result
        ? [
            ...(expected.pairs ?? []).map((p) => ({ ...p, kind: "answer" })),
            ...pairs
              .filter(
                (p) =>
                  !(expected.pairs ?? []).some(
                    (e) => e.leftId === p.leftId && e.rightId === p.rightId,
                  ),
              )
              .map((p) => ({ ...p, kind: "wrong" })),
          ]
        : pairs.map((p) => ({ ...p, kind: "selected" }));
      setLines(
        sets.flatMap((p) => {
          const l = Array.from(
            root.querySelectorAll<HTMLElement>("[data-left]"),
          )
            .find((e) => e.dataset.left === p.leftId)
            ?.getBoundingClientRect();
          const r = Array.from(
            root.querySelectorAll<HTMLElement>("[data-right]"),
          )
            .find((e) => e.dataset.right === p.rightId)
            ?.getBoundingClientRect();
          return l && r
            ? [
                {
                  x1: l.right - bounds.left,
                  y1: l.top + l.height / 2 - bounds.top,
                  x2: r.left - bounds.left,
                  y2: r.top + r.height / 2 - bounds.top,
                  kind: p.kind,
                  key: p.leftId + p.kind,
                },
              ]
            : [];
        }),
      );
    };
    draw();
    const observer = new ResizeObserver(draw);
    observer.observe(board.current);
    window.addEventListener("resize", draw);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", draw);
    };
  }, [draft, result]);
  const previousResult = useRef(result);
  useEffect(() => {
    if (previousResult.current && !result) {
      updateDraft({});
      setView(prepareQuestion(question));
      setActiveLeft(undefined);
      setActiveRight(undefined);
    }
    previousResult.current = result;
  }, [result, question]);
  const ready =
    question.type === "select-many"
      ? selected.length > 0
      : question.type === "match-pairs"
        ? (c.leftItems ?? []).every((i) => pairs.some((p) => p.leftId === i.id))
        : question.type === "order-tap"
          ? order.length === (c.items ?? []).length
          : question.type === "grade-map"
            ? (c.rows ?? []).every((r) => grades[r.id])
            : (c.questions ?? []).every((q) =>
                matrix.some((a) => a.questionId === q.id && a.optionIds.length),
              );
  const matrixOptions = (qid: string) => {
    const actual = matrix.find((a) => a.questionId === qid)?.optionIds ?? [];
    const correct =
      expected.answers?.find((a) => a.questionId === qid)?.optionIds ?? [];
    return (c.options ?? [])
      .filter(
        (o) => actual.includes(o.id) || (!!result && correct.includes(o.id)),
      )
      .map((o) => (
        <span
          key={o.id}
          className={
            "matrix-selected-option feedback--" +
            feedback(o.id, actual, correct)
          }
        >
          {o.text}
          {result ? (
            <small>
              {feedback(o.id, actual, correct) === "correct"
                ? "✓"
                : feedback(o.id, actual, correct) === "missed"
                  ? "漏选"
                  : "×"}
            </small>
          ) : (
            <button
              aria-label={"移除 " + o.text}
              disabled={locked}
              onClick={() =>
                setDraft({
                  ...draft,
                  answers: matrix.map((a) =>
                    a.questionId === qid
                      ? {
                          ...a,
                          optionIds: a.optionIds.filter((id) => id !== o.id),
                        }
                      : a,
                  ),
                })
              }
            >
              ×
            </button>
          )}
        </span>
      ));
  };
  const answerPool = (
    <div className="matrix-pool">
      <strong>
        答案池 · {c.questions?.find((q) => q.id === activeMatrix)?.text}
      </strong>
      <div className="grade-options">
        {c.options?.map((o) => (
          <button
            key={o.id}
            disabled={locked || !activeMatrix}
            className="grade-chip"
            aria-pressed={
              !!matrix
                .find((a) => a.questionId === activeMatrix)
                ?.optionIds.includes(o.id)
            }
            onClick={() => {
              const current =
                matrix.find((a) => a.questionId === activeMatrix)?.optionIds ??
                [];
              const mode =
                c.layout === "comparison-table"
                  ? "single"
                  : (c.questions?.find((q) => q.id === activeMatrix)
                      ?.selectionMode ?? c.selectionMode);
              const next =
                c.layout === "comparison-table"
                  ? [o.id]
                  : current.includes(o.id)
                    ? current.filter((id) => id !== o.id)
                    : mode === "single"
                      ? [o.id]
                      : [...current, o.id];
              setDraft({
                answers: [
                  ...matrix.filter((a) => a.questionId !== activeMatrix),
                  { questionId: activeMatrix!, optionIds: next },
                ],
              });
              if (c.layout === "comparison-table") sheet.current?.close();
            }}
          >
            {o.text}
          </button>
        ))}
      </div>
    </div>
  );
  return (
    <>
      <section className="challenge-card">
        <span className="challenge-selection-mode">
          {question.type === "select-many" && c.selectionMode === "single"
            ? "单选题"
            : labels[question.type]}
        </span>
        <h1 className="challenge-prompt">{question.stem.title}</h1>
        {question.stem.instruction && (
          <p className="challenge-instruction">{question.stem.instruction}</p>
        )}
        {question.type === "select-many" && (
          <div className="knowledge-list">
            {(c.items ?? []).map((item) => (
              <button
                key={item.id}
                disabled={locked}
                aria-pressed={selected.includes(item.id)}
                className={`knowledge-card ${selected.includes(item.id) ? "knowledge-card--selected" : ""} feedback--${feedback(item.id)}`}
                onClick={() =>
                  setDraft({
                    selectedItemIds: selected.includes(item.id)
                      ? selected.filter((id) => id !== item.id)
                      : c.selectionMode === "single"
                        ? [item.id]
                        : [...selected, item.id],
                  })
                }
              >
                <span className="mark-dot">
                  {selected.includes(item.id) ? "✓" : ""}
                </span>
                <span>{item.text}</span>
                {result && feedback(item.id) && (
                  <small className="feedback-note">
                    {feedback(item.id) === "missed"
                      ? "漏圈"
                      : feedback(item.id) === "wrong"
                        ? "×"
                        : "✓"}
                  </small>
                )}
              </button>
            ))}
          </div>
        )}
        {question.type === "match-pairs" && (
          <div className="match-area">
            <p className="match-rule">
              {result
                ? "绿色实线 · 正确答案；红色虚线 · 你的答案"
                : "每道题选择一个对应答案，同一答案可重复使用"}
            </p>
            <div className="match-board" ref={board}>
              <svg className="match-lines" aria-hidden="true">
                {lines.map((l) => (
                  <line
                    key={l.key}
                    {...{ x1: l.x1, y1: l.y1, x2: l.x2, y2: l.y2 }}
                    className={"pair-line " + l.kind}
                  />
                ))}
              </svg>
              <div className="match-column">
                {(c.leftItems ?? []).map((i) => {
                  const actual = pairs.find((p) => p.leftId === i.id)?.rightId;
                  const correct = expected.pairs?.find(
                    (p) => p.leftId === i.id,
                  )?.rightId;
                  return (
                    <button
                      data-left={i.id}
                      key={i.id}
                      disabled={locked}
                      aria-pressed={activeLeft === i.id}
                      className={`match-card ${activeLeft === i.id ? "match-card--active" : ""} ${actual ? "match-card--paired" : ""} feedback--${result ? (actual === correct ? "correct" : "wrong") : ""}`}
                      onClick={() =>
                        activeRight
                          ? choosePair(i.id, activeRight)
                          : setActiveLeft(
                              activeLeft === i.id ? undefined : i.id,
                            )
                      }
                    >
                      {i.text}
                      {result && actual !== correct && (
                        <small>
                          应连{" "}
                          {
                            (c.rightItems ?? []).find((r) => r.id === correct)
                              ?.text
                          }
                        </small>
                      )}
                      <span className="match-endpoint left" />
                    </button>
                  );
                })}
              </div>
              <div className="match-column">
                {(c.rightItems ?? []).map((i) => (
                  <button
                    data-right={i.id}
                    key={i.id}
                    disabled={locked}
                    aria-pressed={activeRight === i.id}
                    className={`match-card ${activeRight === i.id ? "match-card--active" : ""} ${pairs.some((p) => p.rightId === i.id) ? "match-card--paired" : ""}`}
                    onClick={() =>
                      activeLeft
                        ? choosePair(activeLeft, i.id)
                        : setActiveRight(
                            activeRight === i.id ? undefined : i.id,
                          )
                    }
                  >
                    <span className="match-endpoint right" />
                    {i.text}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
        {question.type === "order-tap" && (
          <div className="order-area">
            <div className="order-header">
              <strong>
                我的升级路径 · 已选 {order.length} / {c.items?.length}
              </strong>
              <button
                disabled={locked}
                onClick={() => setDraft({ orderedItemIds: [] })}
              >
                重排
              </button>
            </div>
            <div className="order-lane">
              {order.length
                ? order.map((id) => (
                    <span key={id}>
                      {(c.items?.findIndex((i) => i.id === id) ?? 0) + 1}
                    </span>
                  ))
                : "按顺序点击下方卡片"}
            </div>
            {(c.items ?? []).map((i, n) => (
              <button
                className={`knowledge-card feedback--${result ? (expected.orderedItemIds?.[order.indexOf(i.id)] === i.id ? "correct" : "wrong") : ""}`}
                key={i.id}
                disabled={locked}
                aria-pressed={order.includes(i.id)}
                onClick={() =>
                  setDraft({
                    orderedItemIds: order.includes(i.id)
                      ? order.slice(0, order.indexOf(i.id))
                      : [...order, i.id],
                  })
                }
              >
                <span className="mark-dot">{n + 1}</span>
                {i.text}
              </button>
            ))}
            {result && !result.isCorrect && (
              <p>
                正确顺序：
                {expected.orderedItemIds
                  ?.map((id) => c.items?.find((i) => i.id === id)?.text)
                  .join(" → ")}
              </p>
            )}
          </div>
        )}
        {question.type === "grade-map" && (
          <div className="grade-list">
            {(c.rows ?? []).map((row) => (
              <div
                className={`grade-row feedback--${result ? (grades[row.id] === expected.grades?.[row.id] ? "correct" : "wrong") : ""}`}
                key={row.id}
              >
                <p>{row.text}</p>
                <div className="grade-options">
                  {(c.grades ?? []).map((g) => (
                    <button
                      key={g}
                      disabled={locked}
                      aria-pressed={grades[row.id] === g}
                      className={
                        grades[row.id] === g
                          ? "grade-chip selected"
                          : "grade-chip"
                      }
                      onClick={() =>
                        setDraft({ grades: { ...grades, [row.id]: g } })
                      }
                    >
                      {g}
                    </button>
                  ))}
                </div>
                {result && grades[row.id] !== expected.grades?.[row.id] && (
                  <small>正确等级：{expected.grades?.[row.id]}</small>
                )}
              </div>
            ))}
          </div>
        )}
        {question.type === "matrix-select" && (
          <div className="matrix-list">
            {c.layout === "comparison-table" ? (
              <div className="comparison-scroll">
                <table className="comparison-table">
                  <thead>
                    <tr>
                      <th>{c.rowLabel || "比较项目"}</th>
                      {c.columns?.map((col) => (
                        <th key={col.id}>{col.text}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {c.rows?.map((row) => (
                      <tr key={row.id}>
                        <th>{row.text}</th>
                        {c.columns?.map((col) => {
                          const q = c.questions?.find(
                            (q) => q.rowId === row.id && q.columnId === col.id,
                          );
                          return (
                            <td key={col.id}>
                              {q && (
                                <>
                                  <button
                                    className={
                                      activeMatrix === q.id ? "cell-active" : ""
                                    }
                                    disabled={locked}
                                    onClick={() => {
                                      setActiveMatrix(q.id);
                                      sheet.current?.showModal();
                                    }}
                                  >
                                    {matrixOptions(q.id).length
                                      ? "修改"
                                      : "点击填写"}
                                  </button>
                                  {matrixOptions(q.id)}
                                </>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              (c.questions ?? []).map((q, n) => (
                <div
                  className={`matrix-question ${activeMatrix === q.id ? "matrix-question--active" : ""}`}
                  key={q.id}
                >
                  <button
                    className="matrix-question-title"
                    disabled={locked}
                    onClick={() => setActiveMatrix(q.id)}
                  >
                    {n + 1}. {q.text}{" "}
                    {activeMatrix === q.id && !result ? "· 当前题" : ""}
                  </button>
                  <div>
                    {matrixOptions(q.id).length ? (
                      matrixOptions(q.id)
                    ) : (
                      <small>点击本题，再从下方答案池选择</small>
                    )}
                  </div>
                </div>
              ))
            )}
            {!result &&
              (c.layout === "comparison-table" ? (
                <dialog
                  ref={sheet}
                  className="matrix-sheet"
                  onClick={(e) => {
                    if (e.target === e.currentTarget) sheet.current?.close();
                  }}
                >
                  <button
                    className="sheet-close"
                    aria-label="关闭答案面板"
                    onClick={() => sheet.current?.close()}
                  >
                    ×
                  </button>
                  <h2>选择答案</h2>
                  <p>选中后自动关闭，可继续点选其他单元格</p>
                  {answerPool}
                </dialog>
              ) : (
                answerPool
              ))}
          </div>
        )}
        {result ? (
          <>
            <p
              role="status"
              className={
                "result-status " + (result.isCorrect ? "correct" : "wrong")
              }
            >
              {result.isCorrect ? "✓ 回答正确" : "需要订正"} · {result.score} 分
            </p>
            <button className="answer-retry" disabled={busy} onClick={onRedo}>
              重做
            </button>
          </>
        ) : (
          <button
            form="challenge-answer"
            className="answer-submit"
            disabled={busy || !ready}
            onClick={() => onSubmit(draft)}
          >
            {busy ? "正在提交…" : "提交答案"}
          </button>
        )}
      </section>
      {result && (
        <section className="analysis-section" aria-live="polite">
          <h2>解析</h2>
          {result.feedback.analysisBlocks?.length ? (
            result.feedback.analysisBlocks.map((b, n) =>
              b.type === "image" && b.url ? (
                <ImageAnalysis key={n} url={b.url} caption={b.caption} />
              ) : (
                <p key={n}>{b.text}</p>
              ),
            )
          ) : (
            <p>{result.feedback.analysis || "暂无文字解析"}</p>
          )}
          {result.feedback.takeaway && (
            <>
              <h2>结论</h2>
              <p>{result.feedback.takeaway}</p>
            </>
          )}
        </section>
      )}
    </>
  );
}
